import { CellContext } from '@tanstack/react-table';
import { useStore } from 'zustand';
import { RotateCw, Trash2, Users, XCircle } from 'lucide-react';

import {
  EnvironmentId,
  EnvironmentStatus,
  K8sDistributionType,
  KUBESOLO_PROVIDER,
} from '@/react/portainer/environments/types';
import { useCancelOmniProvisioning } from '@/react/kubernetes/cluster/omni/queries/useCancelOmniProvisioning';
import { notifySuccess } from '@/portainer/services/notifications';
import { environmentStore } from '@/react/hooks/current-environment-store';

import { Button } from '@@/buttons';
import { Link } from '@@/Link';
import { confirmDestructive } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';

import { EnvironmentListItem } from '../types';
import { confirmCancelProvisioning } from '../ConfirmCancelProvisioningModal';
import { confirmDeleteEnvironments } from '../ConfirmDeleteEnvironmentsModal';
import {
  useDeleteEnvironmentsMutation,
  useIsRemovingEnvironments,
} from '../useDeleteEnvironmentsMutation';
import { useRetryKubesoloInstallMutation } from '../useRetryKubesoloInstallMutation';
import { useCancelKubesoloInstallMutation } from '../useCancelKubesoloInstallMutation';

import { columnHelper } from './helper';

// KubeSolo environments' StatusMessage.operation while KubeSolo installs, kept when the install fails
const KUBESOLO_INSTALL_OPERATION = 'install';
// KubeSolo environments' StatusMessage.summary once KubeSolo is installed, when there's nothing left to cancel
const KUBESOLO_WAITING_FOR_AGENT_SUMMARY = 'Waiting for agent';

export const actions = columnHelper.display({
  header: 'Actions',
  cell: Cell,
});

function Cell({
  row: { original: environment },
}: CellContext<EnvironmentListItem, unknown>) {
  const credentialId = environment.CloudProvider?.CredentialID;

  if (
    environment.Status === EnvironmentStatus.Provisioning &&
    environment.CloudProvider?.Provider === K8sDistributionType.OMNI &&
    credentialId
  ) {
    return (
      <CancelProvisioningButton
        environmentId={environment.Id}
        environmentName={environment.Name}
        credentialId={credentialId}
      />
    );
  }

  const isKubesoloInstall =
    environment.CloudProvider?.Provider === KUBESOLO_PROVIDER &&
    environment.StatusMessage?.operation === KUBESOLO_INSTALL_OPERATION;

  // a KubeSolo removal is Provisioning too, and can't be cancelled
  if (
    environment.Status === EnvironmentStatus.Provisioning &&
    isKubesoloInstall &&
    environment.StatusMessage?.summary !== KUBESOLO_WAITING_FOR_AGENT_SUMMARY
  ) {
    return <CancelKubesoloInstallButton environment={environment} />;
  }

  if (environment.Status === EnvironmentStatus.Provisioning) {
    return <>-</>;
  }

  if (environment.Status === EnvironmentStatus.Error) {
    return (
      <div className="flex gap-2">
        {isKubesoloInstall && (
          <RetryKubesoloInstallButton environment={environment} />
        )}
        <RemoveEnvironmentButton environment={environment} />
      </div>
    );
  }

  return (
    <Button
      as={Link}
      props={{
        to: 'portainer.endpoints.endpoint.access',
        params: { id: environment.Id },
      }}
      color="link"
      icon={Users}
      data-cy={`environment-manage-access-button-${environment.Name}`}
    >
      Manage access
    </Button>
  );
}

function CancelProvisioningButton({
  environmentId,
  environmentName,
  credentialId,
}: {
  environmentId: EnvironmentId;
  environmentName: string;
  credentialId: number;
}) {
  const cancelMutation = useCancelOmniProvisioning();

  return (
    <Button
      color="dangerlight"
      icon={XCircle}
      onClick={handleCancel}
      disabled={cancelMutation.isLoading}
      data-cy={`environment-cancel-provisioning-button-${environmentName}`}
    >
      Cancel
    </Button>
  );

  async function handleCancel() {
    const { deleteCluster } =
      (await confirmCancelProvisioning(environmentName)) || {};

    if (deleteCluster === undefined) {
      return;
    }

    cancelMutation.mutate(
      { credentialId, environmentId, deleteCluster },
      {
        onSuccess() {
          notifySuccess('Provisioning cancelled', environmentName);
        },
      }
    );
  }
}

function CancelKubesoloInstallButton({
  environment,
}: {
  environment: EnvironmentListItem;
}) {
  const cancelMutation = useCancelKubesoloInstallMutation();

  return (
    <Button
      color="dangerlight"
      icon={XCircle}
      onClick={handleCancel}
      disabled={cancelMutation.isLoading}
      data-cy={`environment-cancel-provisioning-button-${environment.Name}`}
    >
      {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel'}
    </Button>
  );

  async function handleCancel() {
    const confirmed = await confirmDestructive({
      title: 'Are you sure?',
      message: (
        <>
          Are you sure you want to cancel the install of{' '}
          <b>{environment.Name}</b>? The install stops on the machine, and
          KubeSolo may be partly installed. You can then retry the install, or
          remove the environment and choose to uninstall KubeSolo.
        </>
      ),
      confirmButton: buildConfirmButton('Cancel install', 'danger'),
    });

    if (!confirmed) {
      return;
    }

    cancelMutation.mutate(environment.Id, {
      onSuccess() {
        notifySuccess('Install cancelled', environment.Name);
      },
    });
  }
}

function RetryKubesoloInstallButton({
  environment,
}: {
  environment: EnvironmentListItem;
}) {
  const retryMutation = useRetryKubesoloInstallMutation();
  // A retry would install on the machine while the removal uninstalls from it
  const isRemoving = useIsRemovingEnvironments();

  return (
    <Button
      color="light"
      icon={RotateCw}
      onClick={handleRetry}
      disabled={retryMutation.isLoading || isRemoving}
      data-cy={`environment-retry-install-button-${environment.Name}`}
    >
      Retry
    </Button>
  );

  function handleRetry() {
    retryMutation.mutate(environment.Id, {
      onSuccess() {
        notifySuccess('Install restarted', environment.Name);
      },
    });
  }
}

function RemoveEnvironmentButton({
  environment,
}: {
  environment: EnvironmentListItem;
}) {
  const currentEnvironmentStore = useStore(environmentStore);
  const deletionMutation = useDeleteEnvironmentsMutation();
  const isRemoving = useIsRemovingEnvironments();

  return (
    <Button
      color="dangerlight"
      icon={Trash2}
      onClick={handleRemove}
      disabled={isRemoving}
      data-cy={`environment-remove-button-${environment.Name}`}
    >
      Remove
    </Button>
  );

  async function handleRemove() {
    const { confirmed, deleteClusters } =
      (await confirmDeleteEnvironments([environment])) || {};

    if (!confirmed) {
      return;
    }

    if (currentEnvironmentStore.environmentId === environment.Id) {
      currentEnvironmentStore.clear();
    }

    deletionMutation.mutate([
      {
        id: environment.Id,
        deleteCluster: Boolean(environment.CloudProvider) && deleteClusters,
        name: environment.Name,
      },
    ]);
  }
}
